import { useEffect, useState } from 'react';
import { Pencil, ThumbsUp, ThumbsDown, X } from 'lucide-react';

const API = process.env.REACT_APP_API_URL || '';

export default function EventQnASection({ event: ev, user }) {
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);

  // non-owner Q&A submit form state (based on SubmitQuestionsView)
  const [qaDrafts, setQaDrafts] = useState([]);
  const [qaNew, setQaNew] = useState('');
  const [qaEditingId, setQaEditingId] = useState(null);
  const [qaEditingText, setQaEditingText] = useState('');
  const [qaSubmitting, setQaSubmitting] = useState(false);
  const [qaError, setQaError] = useState(null);

  // owner inline edit/delete state
  const [ownerEditingId, setOwnerEditingId] = useState(null);
  const [ownerEditingText, setOwnerEditingText] = useState('');
  const [ownerSavingId, setOwnerSavingId] = useState(null);
  const [ownerDeletingId, setOwnerDeletingId] = useState(null);
  const [ownerApprovingId, setOwnerApprovingId] = useState(null);

  const matchesByUserId =
    ev?.createdByUserId != null && String(ev.createdByUserId) === String(user?.id);
  const matchesByAuthorName =
    ev?.author === (user?.displayName || user?.username);
  const isAuthor = user && (matchesByUserId || matchesByAuthorName);

  // Check if a question is authored by the logged-in user
  const isQuestionAuthor = (question) => {
    if (!user || !question) return false;
    const userDisplayName = user.displayName || user.username;
    return question.author === userDisplayName;
  };

  const deleteQuestionUrl = (id) =>
    user?.id != null ? `${API}/api/questions/${id}?userId=${encodeURIComponent(String(user.id))}` : `${API}/api/questions/${id}`;

  // Delete handler for user's own questions
  const handleDeleteOwnQuestion = (id) => {
    if (!id || !user) return;
    if (!window.confirm('Haluatko varmasti poistaa tämän kysymyksen?')) return;
    fetch(deleteQuestionUrl(id), { method: 'DELETE' })
      .then((res) => {
        if (!res.ok && res.status !== 204) {
          return res.json().then((err) => {
            throw new Error(err.error || 'Failed to delete question');
          });
        }
        setQuestions((prev) => prev.filter((q) => q.id !== id));
      })
      .catch((err) => {
        console.error('Failed to delete question:', err);
        alert('Kysymyksen poistaminen epäonnistui.');
      });
  };

  useEffect(() => {
    if (!ev?.id) return;
    setLoading(true);
    fetch(`${API}/api/events/${ev.id}/questions`)
      .then((res) => (res.ok ? res.json() : []))
      .then((data) => setQuestions(Array.isArray(data) ? data : []))
      .catch(() => setQuestions([]))
      .finally(() => setLoading(false));
  }, [ev?.id]);

  // owner edit/delete handlers
  const handleOwnerSaveQuestion = (id) => {
    const text = ownerEditingText.trim();
    if (!text || !id) return;
    setOwnerSavingId(id);
    fetch(`${API}/api/questions/${id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ text }),
    })
      .then((res) => {
        if (!res.ok) return res.json().then((err) => { throw new Error(err.error || 'Failed to save question'); });
        return res.json();
      })
      .then((updated) => {
        setQuestions((prev) => prev.map((q) => (q.id === updated.id ? updated : q)));
        setOwnerEditingId(null);
        setOwnerEditingText('');
        setOwnerSavingId(null);
      })
      .catch(() => {
        setOwnerSavingId(null);
      });
  };

  const handleOwnerDeleteQuestion = (id) => {
    if (!id) return;
    if (!window.confirm('Poista tämä kysymys?')) return;
    setOwnerDeletingId(id);
    fetch(deleteQuestionUrl(id), { method: 'DELETE' })
      .then((res) => {
        if (!res.ok && res.status !== 204) {
          return res.json().then((err) => {
            throw new Error(err.error || 'Failed to delete question');
          });
        }
        setQuestions((prev) => prev.filter((q) => q.id !== id));
        setOwnerDeletingId(null);
        if (ownerEditingId === id) {
          setOwnerEditingId(null);
          setOwnerEditingText('');
        }
      })
      .catch(() => {
        setOwnerDeletingId(null);
      });
  };

  const handleOwnerSetQuestionStatus = (id, status) => {
    if (!id || !isAuthor) return;
    setOwnerApprovingId(id);
    fetch(`${API}/api/questions/${id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ status }),
    })
      .then((res) => {
        if (!res.ok) return res.json().then((err) => { throw new Error(err.error || 'Failed to update status'); });
        return res.json();
      })
      .then((updated) => {
        setQuestions((prev) => prev.map((q) => (q.id === updated.id ? updated : q)));
        setOwnerApprovingId(null);
      })
      .catch(() => {
        setOwnerApprovingId(null);
      });
  };

  // non-owner Q&A handlers
  const handleQaAdd = () => {
    const text = qaNew.trim();
    if (!text) return;
    setQaDrafts((prev) => [...prev, { id: Date.now(), text }]);
    setQaNew('');
  };

  const startQaEdit = (q) => {
    setQaEditingId(q.id);
    setQaEditingText(q.text);
  };

  const saveQaEdit = () => {
    if (qaEditingId == null) return;
    const text = qaEditingText.trim();
    setQaDrafts((prev) =>
      text
        ? prev.map((q) => (q.id === qaEditingId ? { ...q, text } : q))
        : prev.filter((q) => q.id !== qaEditingId)
    );
    setQaEditingId(null);
    setQaEditingText('');
  };

  const removeQaDraft = (id) => {
    setQaDrafts((prev) => prev.filter((q) => q.id !== id));
    if (qaEditingId === id) {
      setQaEditingId(null);
      setQaEditingText('');
    }
  };

  const handleQaSubmit = () => {
    if (!ev?.id || !user) return;
    const list = qaDrafts.map((q) => q.text.trim()).filter(Boolean);
    if (list.length === 0) return;
    setQaError(null);
    setQaSubmitting(true);
    fetch(`${API}/api/events/${ev.id}/questions`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        questions: list,
        author: user.displayName || user.username || '',
      }),
    })
      .then((res) => {
        if (!res.ok) return res.json().then((err) => { throw new Error(err.error || 'Failed to submit'); });
        return res.json();
      })
      .then(() => {
        setQaSubmitting(false);
        setQaDrafts([]);
        setQaEditingId(null);
        setQaEditingText('');
        setQaNew('');
        // Refresh questions list
        fetch(`${API}/api/events/${ev.id}/questions`)
          .then((res) => (res.ok ? res.json() : []))
          .then((data) => setQuestions(Array.isArray(data) ? data : []))
          .catch(() => {});
      })
      .catch((err) => {
        setQaError(err.message || 'Failed to submit');
        setQaSubmitting(false);
      });
  };

  return (
    <section className="EventView-questions">
<hr />
      {!ev.enableQA ? (
        
        <p className="EventView-empty">Tälle tapahtumalle ei ole Q&A osiota.</p>
      ) : loading ? (
        <p className="EventView-loading">Ladataan...</p>
      ) : (
        <><h2 className="EventView-qa-title">Q&amp;A</h2>
          {/* Show all questions to everyone */}
          {questions.length === 0 ? (
            <p className="EventView-empty">Ei kysymyksiä vielä...</p>
          ) : (
            <ul className="EventView-questions-list">
              {questions.map((q) => {
                const isQuestionOwner = isQuestionAuthor(q);
                const isEditing = isAuthor && ownerEditingId === q.id;
                const isBusy = isAuthor && (ownerSavingId === q.id || ownerDeletingId === q.id);
                const isApproved = q.status === 'approved';
                const approveBusy = isAuthor && ownerApprovingId === q.id;
                const removeDisabled = isApproved; // approved questions must be disapproved first
                return (
                  <li
                    key={q.id}
                    className={`EventView-question${isEditing ? ' EventView-question--editing' : ''}${isApproved ? ' EventView-question--approved' : ''}`}
                    onClick={() => {
                      if (isAuthor) {
                        setOwnerEditingId(q.id);
                        setOwnerEditingText(q.text);
                      }
                    }}
                  >
                    <div className="EventView-question-main">
                      {isEditing ? (
                        <input
                          type="text"
                          value={ownerEditingText}
                          onChange={(e) => setOwnerEditingText(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') handleOwnerSaveQuestion(q.id);
                            if (e.key === 'Escape') {
                              setOwnerEditingId(null);
                              setOwnerEditingText('');
                            }
                          }}
                          className="EventView-question-edit-input"
                          autoFocus
                        />
                      ) : (
                        <>
                          <span className="EventView-question-text">{q.text}</span>
                          <span className="EventView-question-meta">— {q.author}</span>
                        </>
                      )}
                    </div>
                    <div className="EventView-question-actions">
                      {/* Event author: approve (thumb up) or disapprove (thumb down) */}
                      {isAuthor && (
                        <button
                          type="button"
                          className={`EventView-question-icon-btn EventView-question-approve-btn${isApproved ? ' EventView-question-approve-btn--active' : ''}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (approveBusy) return;
                            handleOwnerSetQuestionStatus(q.id, isApproved ? 'disapproved' : 'approved');
                          }}
                          disabled={approveBusy}
                          aria-label={isApproved ? 'Disapprove question' : 'Approve question'}
                          title={isApproved ? 'Disapprove question (allows removal)' : 'Approve question'}
                        >
                          {isApproved ? (
                            <ThumbsDown className="EventView-question-approve-icon" size={24} strokeWidth={2} />
                          ) : (
                            <ThumbsUp className="EventView-question-approve-icon" size={24} strokeWidth={2} />
                          )}
                        </button>
                      )}
                      {/* Delete for event author or question author; disabled when approved */}
                      {(isAuthor || isQuestionOwner) && (
                        <button
                          type="button"
                          className={`EventView-question-icon-btn EventView-question-remove-btn${isEditing ? ' EventView-question-icon-btn--save' : ''}${removeDisabled ? ' EventView-question-remove-btn--disabled' : ''}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (removeDisabled) return;
                            if (isAuthor && isEditing) {
                              handleOwnerSaveQuestion(q.id);
                            } else if (isAuthor) {
                              handleOwnerDeleteQuestion(q.id);
                            } else if (isQuestionOwner) {
                              handleDeleteOwnQuestion(q.id);
                            }
                          }}
                          disabled={isBusy || removeDisabled}
                          aria-label={isEditing ? 'Save question' : isAuthor ? 'Remove question' : 'Delete your question'}
                          title={removeDisabled ? 'Disapprove the question first to remove it' : (isEditing ? 'Save question' : isAuthor ? 'Remove question' : 'Delete your question')}
                        >
                          <X className="EventView-question-delete-icon" size={24} strokeWidth={2} />
                        </button>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
          {/* Show edit form only for event authors */}
          {isAuthor ? null : (
        <div className="EventView-qa-form">
          <div className="EventView-qa-add">
          <hr />
          <h2>Esitä Kysymyksiä</h2>

            <input
              type="text"
              value={qaNew}
              onChange={(e) => setQaNew(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleQaAdd())}
              placeholder="Lisää kysymyksesi tähän.."
              className="EventView-qa-input"
            />
            <button type="button" className="EventView-qa-add-btn" onClick={handleQaAdd}>
              +
            </button>
          </div>

          <ul className="EventView-qa-list">
            {qaDrafts.map((q) => (
              <li key={q.id} className="EventView-qa-item">
                {qaEditingId === q.id ? (
                  <>
                    <input
                      type="text"
                      value={qaEditingText}
                      onChange={(e) => setQaEditingText(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') saveQaEdit();
                        if (e.key === 'Escape') {
                          setQaEditingId(null);
                          setQaEditingText('');
                        }
                      }}
                      className="EventView-qa-input EventView-qa-edit-input"
                      autoFocus
                    />
                    <button type="button" className="EventView-qa-save-edit" onClick={saveQaEdit}>
                      Save
                    </button>
                  </>
                ) : (
                  <>
                    <span className="EventView-qa-item-text">{q.text}</span>
                    <div className="EventView-qa-item-actions">
                      <button type="button" className="Button EventView-qa-edit-btn" onClick={() => startQaEdit(q)}>
                        <Pencil className="EventView-qa-edit-icon" size={16} strokeWidth={2} />
                      </button>
                      <button type="button" className="Button EventView-qa-remove-btn" onClick={() => removeQaDraft(q.id)}>
                        X
                      </button>
                    </div>
                  </>
                )}
              </li>
            ))}
          </ul>
          {qaError && <p className="EventView-qa-error">{qaError}</p>}
          <div className="EventView-qa-actions">
            <button
              type="button"
              className={`EventView-qa-submit${qaDrafts.length === 0 ? '-disabled' : ''}`}
              onClick={handleQaSubmit}
              disabled={qaDrafts.length === 0 || qaSubmitting}
              title={qaDrafts.length === 0 ? 'Lisää kysymyksiä' : 'Lähetä kysymykset'}
            >
              {qaSubmitting ? 'Submitting…' : 'Submit'}
            </button>
          </div>
        </div>
          )}
        </>
      )}
    </section>
  );
}